import * as XLSX from 'xlsx-js-style';
import { WarehouseReceipt } from '@/types/warehouseReceipt';
import { warehouseReceiptApi } from '@/api/endpoints/warehouseReceipts';

interface ExcelExportOptions {
  t: (key: string) => string;
  getStatusLabel: (status: string) => string;
}

/**
 * Service for exporting warehouse receipts to Excel format
 */
export class WarehouseReceiptExcelExport {
  private options: ExcelExportOptions;

  constructor(options: ExcelExportOptions) {
    this.options = options;
  }

  /**
   * Exports warehouse receipts for a specific supplier to Excel
   */
  async exportSupplierReceipts(receipt: WarehouseReceipt): Promise<void> {
    if (!this.canExport(receipt)) {
      throw new Error('Complete supplier and cost data is required for export');
    }

    try {
      // Get all warehouse receipts for this supplier
      const supplierReceipts = await warehouseReceiptApi.getAll({
        supplier_id: receipt.code,
        page_size: 1000, // Get all receipts
      });

      const excelData = this.prepareExcelData(supplierReceipts.data);
      const workbook = this.createWorkbook(excelData);
      const filename = this.generateFilename(receipt);

      // Save the file
      XLSX.writeFile(workbook, filename);

      console.log(this.options.t('warehouseReceipts.messages.exportSuccess'));
    } catch (error) {
      console.error(this.options.t('warehouseReceipts.messages.exportError'), error);
      throw error;
    }
  }

  /**
   * Prepares data for Excel export with localized column headers
   */
  private prepareExcelData(receipts: WarehouseReceipt[]): Record<string, any>[] {
    const { t, getStatusLabel } = this.options;

    return receipts.map((wr) => ({
      [t('warehouseReceipts.quickView.excel.receiptCode')]: wr.code,
      [t('warehouseReceipts.quickView.excel.receiptTime')]: wr.receipt_date 
        ? new Date(wr.receipt_date).toLocaleDateString('vi-VN') 
        : '-',
      [t('warehouseReceipts.quickView.excel.branch')]: t('warehouseReceipts.quickView.defaultBranch'),
      [t('warehouseReceipts.quickView.excel.supplier')]: wr.supplier 
        ? `${wr.supplier.name} (${wr.supplier.code})` 
        : wr.supplier_id,
      [t('warehouseReceipts.quickView.excel.creator')]: wr.created_by_name || wr.created_by || '-',
      [t('warehouseReceipts.quickView.excel.totalAmount')]: this.requiredAmount(wr.total_amount),
      [t('warehouseReceipts.quickView.excel.receiptStatus')]: getStatusLabel(wr.status),
      [t('warehouseReceipts.quickView.excel.note')]: wr.note || '-',
    }));
  }

  /**
   * Creates Excel workbook with formatted data
   */
  private createWorkbook(excelData: Record<string, any>[]): XLSX.WorkBook {
    const { t } = this.options;

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 15 }, // Receipt Code
      { wch: 20 }, // Receipt Time
      { wch: 15 }, // Branch
      { wch: 30 }, // Supplier
      { wch: 20 }, // Creator
      { wch: 15 }, // Total Amount
      { wch: 15 }, // Receipt Status
      { wch: 30 }, // Note
    ];
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, t('warehouseReceipts.quickView.worksheetName'));

    return workbook;
  }

  /**
   * Generates a descriptive filename for the Excel export
   */
  private generateFilename(receipt: WarehouseReceipt): string {
    const supplierName = receipt.supplier?.name || receipt.supplier_id || '';
    
    // Clean supplier name for filename (remove special characters)
    const cleanSupplierName = supplierName
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase();
    
    // Use current date in YYYY-MM-DD format
    const currentDate = new Date().toISOString().split('T')[0];
    
    return `${cleanSupplierName}_${currentDate}.xlsx`;
  }

  /**
   * Validates if export is possible for the given receipt
   */
  canExport(receipt: WarehouseReceipt | null): boolean {
    return !!(receipt && receipt.supplier_id && typeof receipt.total_amount === 'number' && Number.isFinite(receipt.total_amount));
  }

  private requiredAmount(value?: number): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('Incomplete warehouse cost data');
    return value;
  }
}
